import { Alert, Box, Button, Divider, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { PASSWORD_MIN_LENGTH } from './authConstants';
import { trackSignUp } from './analytics';
import { handleGoogleLoginSuccess, registerWithEmail, renderGoogleLoginButton } from './LoginUtils';

export default function Register() {
    const navigate = useNavigate();
    const { setAuthenticatedUser } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        renderGoogleLoginButton('googleRegister', onGoogleSuccess, { text: 'signup_with' });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function onGoogleSuccess(response) {
        handleGoogleLoginSuccess(response, (user) => {
            setAuthenticatedUser(user);
            navigate('/mycases');
        }, { authEvent: 'sign_up' }).catch((err) => {
            setError(err.message);
        });
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSubmitting(true);
        try {
            const result = await registerWithEmail({ email, password, name });
            trackSignUp('email');
            setSuccess(result.message);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'var(--background-color)',
                color: 'var(--text-primary)',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '440px', px: 2 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1.5, textAlign: 'center' }}>
                    Create your account
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: 'var(--text-secondary)', mb: 3, textAlign: 'center', lineHeight: 1.6 }}
                >
                    Get started with secure access to your case workspace
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: '340px' }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2, width: '100%', maxWidth: '340px' }}>{success}</Alert>}

                {!success && (
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}
                    >
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            fullWidth
                            size="small"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            fullWidth
                            sx={{
                                background: 'var(--button-gradient)',
                                color: 'white',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '8px',
                                py: 1.2,
                                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
                                '&:hover': {
                                    background: 'var(--button-gradient-hover)',
                                    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                                },
                                '&:disabled': {
                                    background: 'var(--text-secondary)',
                                    opacity: 0.5,
                                    color: 'white',
                                },
                            }}
                        >
                            {submitting ? 'Creating account…' : 'Create account'}
                        </Button>
                        <Typography variant="caption" sx={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                            Password must be at least {PASSWORD_MIN_LENGTH} characters
                        </Typography>
                    </Box>
                )}

                {success && (
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/mycases')}
                        sx={{
                            mt: 1,
                            textTransform: 'none',
                            borderColor: 'var(--border-color)',
                            color: 'var(--text-primary)',
                            '&:hover': { borderColor: 'var(--accent-color)', background: 'var(--surface-hover)' },
                        }}
                    >
                        Go to sign in
                    </Button>
                )}

                {!success && (
                    <>
                        <Divider sx={{ width: '100%', maxWidth: '340px', my: 2 }}>or</Divider>

                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '340px',
                                borderRadius: '14px',
                                padding: '20px 24px',
                                background: 'linear-gradient(145deg, rgba(244, 208, 63, 0.12) 0%, rgba(212, 175, 55, 0.06) 100%)',
                                border: '2px solid var(--accent-color)',
                            }}
                        >
                            <div id="googleRegister" />
                        </Box>
                    </>
                )}

                <Typography
                    variant="body2"
                    sx={{ mt: 3, textAlign: 'center', color: 'var(--text-secondary)' }}
                >
                    Already have an account? <Link to="/mycases">Sign in</Link>
                </Typography>

                <Typography
                    component="div"
                    sx={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', mt: 2 }}
                >
                    By creating an account, you agree to our{' '}
                    <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                </Typography>
            </Box>
        </div>
    );
}
